import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import EditCriteriaPanel from '../EditCriteriaPanel';
import { AlarmEventTreeTable } from '../alarm-event/AlarmEventTreeTable';
import { AlarmRuleForm } from './AlarmRuleForm';
import { DATA_WINDOW_UNITS, getAlarmRuleFormError, useAlarmRuleForm, useAlarmRuleWindow } from './useAlarmRuleForm';
import { alarmIntervalToSeconds, splitAlarmInterval } from './alarm-interval';
import { getAlarmRuleCriteriaErrors } from './alarm-rule-validation';
import {
  MAX_DATA_WINDOW_SECONDS,
  createSelection,
  dataWindowSecondsToValue,
  formatDataWindowValue,
  getAlarmRuleErrorMessage,
  selectionFingerprint,
  selectionToEventFilter,
} from '../filterParameterMapping';

// Rules store only explicitly selected cars; expanding the consist into its fleet car list adds a
// car_number filter the hourly runner does not apply and can drop events from the report.
const toRuleEventFilter = (selection, overrides = {}) => selectionToEventFilter(selection, {
  ...overrides,
  cars: (selection?.cars || []).map(car => car?.value ?? car?.id ?? car),
});

const emptyOptions = () => ({ consistOptions: [], carOptions: [], subsystemOptions: [], eventOptions: [], envDataFields: [], envDataFieldValues: {}, envDataFieldMetadata: [] });

const getDisabledReasons = ({ criteriaLoading, criteriaError, queryError, intervalTooLong, windowError, state, saving, reportLoading }) => {
  const intervalError = intervalTooLong ? 'Must not exceed the data window.' : '';
  const savingReason = saving ? 'Saving...' : '';
  const reportReason = reportLoading ? 'Loading report results...' : '';
  const criteriaReason = criteriaLoading ? 'Loading rule criteria...' : criteriaError || queryError || intervalError;
  const { form } = state;
  const reportFieldsReason = Object.values(getAlarmRuleCriteriaErrors(form)).find(Boolean) || '';
  return {
    disabledReason: state.ruleTypesLoading ? 'Loading rule types...'
      : state.getRuleTypeError() || getAlarmRuleFormError(form) || savingReason || windowError || criteriaReason,
    reportDisabledReason: savingReason || criteriaReason || reportReason || windowError || reportFieldsReason,
  };
};

const mapCriteriaOptions = ([consists, cars, subsystems, events, metadata]) => {
  const envDataFieldMetadata = (metadata || []).map(field => ({
    name: field.element_display_name || field.name,
    displayName: field.element_display_name || field.displayName,
    variableType: field.element_var_type || field.variableType,
    bitSize: field.element_bit_size || field.bitSize,
    offset: field.element_offset || field.offset,
    translationReference: field.element_translation_reference,
    hasTranslation: !!field.element_translation_reference,
  }));
  return {
    consistOptions: (consists || []).map(consist => ({ title: consist.trainId, value: consist.trainId, cars: consist.cars || [] })),
    carOptions: (cars || []).map(car => ({ title: car, value: car })),
    subsystemOptions: subsystems || [],
    eventOptions: (events || []).map(event => ({ title: event.name, value: event.id, subsystem: event.subsystemName, subsystemId: event.subsystemId })),
    envDataFieldMetadata,
    envDataFields: envDataFieldMetadata.map(field => field.name),
    envDataFieldValues: Object.fromEntries(envDataFieldMetadata.map(field => [field.name, field.variableType === 'BOOL' ? ['true', 'false'] : []])),
  };
};

export const EditAlarmRuleModal = ({ rule, onSaved, onCancel, alarmRuleService, alarmRuleReportService, analysisService, fleetService, accountService, alarmNotificationService, __env }) => {
  const showAdvancedFilters = __env.profile === 'LU';
  const state = useAlarmRuleForm({
    initialForm: () => {
      const interval = splitAlarmInterval(rule.alarmIntervalSeconds);
      return {
        ruleName: rule.ruleName,
        ruleTypeId: rule.ruleTypeId ?? null,
        description: rule.description,
        alarmThresholdCount: rule.alarmThresholdCount,
        alarmIntervalValue: interval.value,
        alarmIntervalUnit: interval.unit,
        isActive: rule.isActive !== false,
        ownerText: rule.ownerText || '',
      };
    },
    initialSubscriberIds: rule.subscribedUserIds || [],
    alarmRuleService,
    accountService,
    alarmNotificationService,
  });
  const defaultSeconds = Math.max(1, Number(rule.dataWindowSeconds || 0)) || 72 * 3600;
  const window = useAlarmRuleWindow(null, defaultSeconds / 3600, true);
  const [options, setOptions] = useState(emptyOptions);
  const [criteriaLoading, setCriteriaLoading] = useState(true);
  const [criteriaError, setCriteriaError] = useState(null);
  const [queryValid, setQueryValid] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const keepEditingRef = useRef(null);
  useEffect(() => {
    if (confirmDiscard) keepEditingRef.current?.focus();
  }, [confirmDiscard]);
  const [report, setReport] = useState(null);
  const [reportCriteria, setReportCriteria] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const initialFingerprint = useRef(null);
  const savingRef = useRef(false);
  const reportGeneration = useRef(0);
  const reportId = useRef(null);
  const { form, subscriberIds, mountedRef } = state;
  const { selection, setSelection } = window;
  const [reportLoaders] = useState(() => ({
    occurrences: params => alarmRuleReportService.getReportRunOccurrences(reportId.current, params),
    events: ({ occurrenceIndex, ...params }) => alarmRuleReportService.getReportRunOccurrenceEvents(reportId.current, occurrenceIndex, params),
  }));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let nextOptions = emptyOptions();
      try {
        nextOptions = mapCriteriaOptions(await Promise.all([
          fleetService.getTrainsWithCars(), analysisService.carList(), analysisService.subsystemInfo(),
          analysisService.eventInfo(), analysisService.envDataVariables(),
        ]));
      } catch (error) {
        if (!cancelled && mountedRef.current) setCriteriaError(getAlarmRuleErrorMessage(error, 'Some rule criteria could not be loaded.'));
      }
      if (cancelled || !mountedRef.current) return;
      const nextSelection = createSelection({ rule, ...nextOptions });
      if (nextSelection.dataWindowMode === 'date') {
        nextSelection.dataWindowMode = 'hours';
        nextSelection.dataWindowValue = formatDataWindowValue(dataWindowSecondsToValue(defaultSeconds, 'hours'));
        nextSelection.dataWindowSeconds = defaultSeconds;
      }
      initialFingerprint.current = selectionFingerprint(nextSelection);
      setOptions(nextOptions);
      setSelection(nextSelection);
      setCriteriaLoading(false);
    };
    void load();
    return () => { cancelled = true; reportGeneration.current += 1; };
  }, [rule, defaultSeconds, fleetService, analysisService, mountedRef, setSelection]);

  const intervalSeconds = alarmIntervalToSeconds(form.alarmIntervalValue, form.alarmIntervalUnit);
  const intervalTooLong = intervalSeconds > window.seconds;
  const dataWindowTooLarge = window.seconds > MAX_DATA_WINDOW_SECONDS;
  const queryError = !queryValid && selection?.envFilters?.query?.rules?.length ? 'Complete the environment query before continuing.' : '';
  const { disabledReason, reportDisabledReason } = getDisabledReasons({
    criteriaLoading, criteriaError, queryError, intervalTooLong, windowError: window.error, state, saving, reportLoading,
  });
  const criteriaChanged = !criteriaLoading && initialFingerprint.current != null && (
    Number(form.alarmThresholdCount) !== Number(rule.alarmThresholdCount)
    || intervalSeconds !== rule.alarmIntervalSeconds || selectionFingerprint(selection) !== initialFingerprint.current
  );
  const originalSubscriberIds = (rule.subscribedUserIds || []).map(Number);
  const hasChanges = form.ruleName !== rule.ruleName || form.ruleTypeId !== (rule.ruleTypeId ?? null)
    || form.description !== rule.description || criteriaChanged || form.isActive !== (rule.isActive !== false)
    || form.ownerText !== (rule.ownerText || '')
    || subscriberIds.slice().sort((left, right) => left - right).join(',') !== originalSubscriberIds.slice().sort((left, right) => left - right).join(',');
  const reportFingerprint = selection ? JSON.stringify([
    selectionFingerprint(selection), Number(form.alarmThresholdCount), intervalSeconds, window.seconds,
  ]) : '';
  const reportStale = reportCriteria !== null && reportCriteria !== reportFingerprint;

  const submit = async () => {
    if (disabledReason || !hasChanges || savingRef.current || !mountedRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError(null);
    try {
      const filter = toRuleEventFilter(selection);
      await alarmRuleService.edit(rule.id, {
        ruleName: form.ruleName,
        ruleTypeId: form.ruleTypeId,
        description: form.description,
        alarmThresholdCount: form.alarmThresholdCount,
        alarmIntervalSeconds: intervalSeconds,
        dataWindowSeconds: window.seconds,
        consists: selection.consist ? [selection.consist.value] : [],
        cars: filter.cars,
        subsystemIds: filter.subsystemIds,
        eventNumbers: filter.eventIds,
        environmentDataQuery: selection.envFilters.filterJson || null,
        isActive: form.isActive,
        ownerText: form.ownerText,
        subscribedUserIds: subscriberIds,
      });
      if (!mountedRef.current) return;
      if (criteriaChanged) alarmNotificationService.success('A new rule version was created and the current rule was archived successfully');
      onSaved();
    } catch (error) {
      if (!mountedRef.current) return;
      const message = getAlarmRuleErrorMessage(error, 'The alarm rule could not be saved. Please try again.');
      setSubmitError(message);
      alarmNotificationService.error('Failed to save alarm rule', message);
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const runReport = async () => {
    if (reportDisabledReason || !selection || !mountedRef.current) return;
    const generation = ++reportGeneration.current;
    reportId.current = null;
    setReportCriteria(reportFingerprint);
    setReport(null);
    setReportLoading(true);
    setReportError(null);
    try {
      // Anchor the relative window at click time; the selection dates may be stale defaults.
      const endDate = DateTime.utc().startOf('second');
      const result = await alarmRuleReportService.runAsReportPaged({
        occurrence: Number(form.alarmThresholdCount),
        intervalSeconds,
        dataWindowSeconds: window.seconds,
        eventFilter: toRuleEventFilter(selection, {
          eventSortType: null,
          environmentDataQuery: selection.envFilters.filterJson || null,
          startDate: endDate.minus({ seconds: window.seconds }).toISO(),
          endDate: endDate.toISO(),
        }),
      });
      if (!mountedRef.current || generation !== reportGeneration.current) return;
      reportId.current = result.reportRunId;
      setReport(result);
    } catch (error) {
      if (!mountedRef.current || generation !== reportGeneration.current) return;
      const message = getAlarmRuleErrorMessage(error, 'Failed to run alarm rule report.');
      setReportError(message);
      alarmNotificationService.error('Failed to run alarm rule report', message);
    } finally {
      if (mountedRef.current && generation === reportGeneration.current) setReportLoading(false);
    }
  };

  const close = () => {
    mountedRef.current = false;
    reportGeneration.current += 1;
    onCancel();
  };
  const cancel = () => {
    if (savingRef.current) return;
    if (hasChanges) {
      setConfirmDiscard(true);
      return;
    }
    close();
  };
  const panelProps = {
    ...options,
    selection,
    dateWindowMode: selection?.dataWindowMode,
    dateWindowValue: selection?.dataWindowValue,
    dateWindowUnits: DATA_WINDOW_UNITS,
    dateWindowDefaultHours: defaultSeconds / 3600,
    validateAlarmWindow: true,
    onSelectionChange: setSelection,
    onEnvFiltersChanged: envFilters => setSelection(previous => ({ ...previous, envFilters })),
    onEnvQueryValidityChange: setQueryValid,
    onDateWindowModeChange: window.changeMode,
    onDateWindowValueChange: window.changeValue,
    onDateWindowReset: window.reset,
  };
  const showCriteria = !criteriaLoading && !criteriaError && !!selection;
  const hasReportResults = !!report && report.occurrenceTotalCount !== 0 && !!report.uniqueConsistList?.length;

  return (
    <AlarmRuleForm mode="edit" state={state} saving={saving} submitError={submitError} entityId={rule.id} disabledReason={disabledReason || (!hasChanges ? 'No changes to save' : '')}
      onSubmit={submit} onCancel={cancel} intervalTooLong={intervalTooLong} dataWindowTooLarge={dataWindowTooLarge}
      criteriaLoading={criteriaLoading} criteriaChanged={criteriaChanged} onRunReport={runReport} reportDisabledReason={reportDisabledReason}
      cancelConfirmation={confirmDiscard ? (
        <div className="alert alert-warning alarm-rule-form__discard-confirmation" role="alert" data-testid="alarm-rule-edit-discard-confirmation">
          <p>Discard your unsaved changes?</p>
          <button type="button" className="btn btn-secondary" onClick={() => setConfirmDiscard(false)} ref={keepEditingRef} data-testid="alarm-rule-edit-keep-editing">
            <i className="fas fa-edit fa-fw" aria-hidden="true" /> Keep editing
          </button>{' '}
          <button type="button" className="btn btn-danger" onClick={close} data-testid="alarm-rule-edit-discard-changes">
            <i className="fas fa-trash fa-fw" aria-hidden="true" /> Discard changes
          </button>
        </div>
      ) : null}
      reportContent={(
      <div data-testid="alarm-rule-edit-report-results">
        {reportStale && (
          <output className="alert alert-warning" data-testid="alarm-rule-edit-report-stale">
            Criteria changed. Run Report again for the current criteria.
          </output>
        )}
        {reportLoading && <p className="help-block" data-testid="alarm-rule-edit-report-loading">Loading report results...</p>}
        {reportError && <p className="text-danger" data-testid="alarm-rule-edit-report-error">{reportError}</p>}
        {!reportLoading && !reportError && report && !hasReportResults && <p className="help-block" data-testid="alarm-rule-edit-report-empty">No alarm report results found.</p>}
        {!reportLoading && !reportError && hasReportResults && (
          <div data-testid="alarm-rule-edit-report-results-tree">
            <AlarmEventTreeTable alarmEvents={[report]} lazyLoading onLoadOccurrences={reportLoaders.occurrences}
              onLoadOccurrenceEvents={reportLoaders.events} rootLevel="occurrence" toolbarTitle="Report Results" />
          </div>
        )}
      </div>
      )}
      dataWindowControl={showCriteria ? <EditCriteriaPanel {...panelProps} showCriteriaFilters={false} showEnvironmentQuery={false}
        dateWindowErrorId={dataWindowTooLarge ? 'alarm-rule-edit-data-window-error' : undefined} /> : null}>
      {criteriaLoading && <p className="help-block" data-testid="alarm-rule-edit-criteria-loading">Loading rule criteria...</p>}
      {criteriaError && <p className="text-danger" data-testid="alarm-rule-edit-criteria-error">{criteriaError}</p>}
      {showCriteria && <EditCriteriaPanel {...panelProps} showDates={false} showEnvGetDataButton={false}
        showConsist={showAdvancedFilters} showEnvironmentQuery={showAdvancedFilters} initiallyCollapseEnvironmentQuery />}
    </AlarmRuleForm>
  );
};

EditAlarmRuleModal.propTypes = {
  __env: PropTypes.object.isRequired,
  rule: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  alarmRuleService: PropTypes.object.isRequired,
  alarmRuleReportService: PropTypes.object.isRequired,
  analysisService: PropTypes.object.isRequired,
  fleetService: PropTypes.object.isRequired,
  accountService: PropTypes.object.isRequired,
  alarmNotificationService: PropTypes.object.isRequired,
};